import React from 'react';
import { Mail, Phone, MapPin, Send, MessageCircle } from 'lucide-react';
import { motion } from 'framer-motion';

const Contact = () => {
  return (
    <section id="contact" className="py-32 bg-white relative overflow-hidden">
      {/* Decorative background title */}
      <div className="absolute top-20 right-0 whitespace-nowrap pointer-events-none select-none opacity-[0.02]">
        <h2 className="text-[25vw] font-serif leading-none tracking-tighter text-luxury-black">CONTACT</h2>
      </div>

      <div className="container-custom relative z-10">
        <div className="flex flex-col lg:flex-row gap-20">
          
          {/* Info Side */}
          <div className="w-full lg:w-1/3">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <span className="text-orange-500 text-xs tracking-[0.5em] uppercase font-black mb-6 block">
                Connect With Us
              </span>
              <h2 className="text-6xl md:text-7xl font-serif text-luxury-black mb-10 leading-tight">
                Let's Start a <br/>Conversation
              </h2>
              
              <div className="space-y-12">
                <div className="group cursor-pointer">
                  <div className="flex items-center gap-6 mb-4">
                    <div className="w-12 h-12 rounded-2xl bg-gray-50 flex items-center justify-center group-hover:bg-orange-500 transition-colors duration-500">
                      <MapPin className="w-5 h-5 text-luxury-black group-hover:text-white transition-colors" />
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-gray-400">Our Location</span>
                  </div>
                  <p className="text-luxury-black text-lg font-light pl-18 leading-relaxed">Malakpet, Hyderabad, <br/>Telangana 500036</p>
                </div>

                <div className="group cursor-pointer">
                  <div className="flex items-center gap-6 mb-4">
                    <div className="w-12 h-12 rounded-2xl bg-gray-50 flex items-center justify-center group-hover:bg-orange-500 transition-colors duration-500">
                      <Phone className="w-5 h-5 text-luxury-black group-hover:text-white transition-colors" />
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-gray-400">Phone & WhatsApp</span>
                  </div>
                  <p className="text-luxury-black text-lg font-light pl-18">+91 40 1234 5678</p>
                </div>

                <div className="group cursor-pointer">
                  <div className="flex items-center gap-6 mb-4">
                    <div className="w-12 h-12 rounded-2xl bg-gray-50 flex items-center justify-center group-hover:bg-orange-500 transition-colors duration-500">
                      <Mail className="w-5 h-5 text-luxury-black group-hover:text-white transition-colors" />
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-gray-400">Email Us</span>
                  </div>
                  <p className="text-luxury-black text-lg font-light pl-18">reservations@gphubluxurysuites.com</p>
                </div>
              </div>

              <div className="mt-16 flex gap-4">
                <button className="bg-green-500 text-white px-8 py-4 rounded-2xl flex items-center gap-3 font-bold text-[10px] tracking-widest uppercase hover:bg-green-600 transition-all shadow-lg hover:shadow-green-500/20">
                  <MessageCircle className="w-4 h-4" />
                  Chat on WhatsApp
                </button>
              </div>
            </motion.div>
          </div>

          {/* Form Side */}
          <div className="w-full lg:w-2/3">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-gray-50 rounded-[3rem] p-10 md:p-20 shadow-sm"
            >
              <form className="space-y-12">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                  <div className="relative group">
                    <input type="text" id="name" className="peer w-full bg-transparent border-b-2 border-gray-200 py-4 outline-none focus:border-orange-500 transition-all font-light text-xl text-luxury-black placeholder-transparent" placeholder="Name" />
                    <label htmlFor="name" className="absolute left-0 top-4 text-gray-400 text-xl font-light pointer-events-none transition-all peer-focus:-top-6 peer-focus:text-xs peer-focus:uppercase peer-focus:tracking-[0.3em] peer-focus:text-orange-500 peer-[:not(:placeholder-shown)]:-top-6 peer-[:not(:placeholder-shown)]:text-xs peer-[:not(:placeholder-shown)]:uppercase peer-[:not(:placeholder-shown)]:tracking-[0.3em]">Full Name</label>
                  </div>
                  <div className="relative group">
                    <input type="email" id="email" className="peer w-full bg-transparent border-b-2 border-gray-200 py-4 outline-none focus:border-orange-500 transition-all font-light text-xl text-luxury-black placeholder-transparent" placeholder="Email" />
                    <label htmlFor="email" className="absolute left-0 top-4 text-gray-400 text-xl font-light pointer-events-none transition-all peer-focus:-top-6 peer-focus:text-xs peer-focus:uppercase peer-focus:tracking-[0.3em] peer-focus:text-orange-500 peer-[:not(:placeholder-shown)]:-top-6 peer-[:not(:placeholder-shown)]:text-xs peer-[:not(:placeholder-shown)]:uppercase peer-[:not(:placeholder-shown)]:tracking-[0.3em]">Email Address</label>
                  </div>
                </div>

                <div className="relative group">
                  <input type="text" id="subject" className="peer w-full bg-transparent border-b-2 border-gray-200 py-4 outline-none focus:border-orange-500 transition-all font-light text-xl text-luxury-black placeholder-transparent" placeholder="Subject" />
                  <label htmlFor="subject" className="absolute left-0 top-4 text-gray-400 text-xl font-light pointer-events-none transition-all peer-focus:-top-6 peer-focus:text-xs peer-focus:uppercase peer-focus:tracking-[0.3em] peer-focus:text-orange-500 peer-[:not(:placeholder-shown)]:-top-6 peer-[:not(:placeholder-shown)]:text-xs peer-[:not(:placeholder-shown)]:uppercase peer-[:not(:placeholder-shown)]:tracking-[0.3em]">How can we help?</label>
                </div>

                <div className="relative group">
                  <textarea id="message" rows="4" className="peer w-full bg-transparent border-b-2 border-gray-200 py-4 outline-none focus:border-orange-500 transition-all font-light text-xl text-luxury-black placeholder-transparent resize-none" placeholder="Message"></textarea>
                  <label htmlFor="message" className="absolute left-0 top-4 text-gray-400 text-xl font-light pointer-events-none transition-all peer-focus:-top-6 peer-focus:text-xs peer-focus:uppercase peer-focus:tracking-[0.3em] peer-focus:text-orange-500 peer-[:not(:placeholder-shown)]:-top-6 peer-[:not(:placeholder-shown)]:text-xs peer-[:not(:placeholder-shown)]:uppercase peer-[:not(:placeholder-shown)]:tracking-[0.3em]">Your Message</label>
                </div>

                <button className="group relative bg-luxury-black text-white px-12 py-6 rounded-2xl overflow-hidden shadow-2xl transition-all hover:-translate-y-1 active:translate-y-0">
                  <div className="absolute inset-0 bg-orange-500 translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
                  <span className="relative z-10 flex items-center gap-3 font-bold text-xs tracking-[0.3em] uppercase">
                    Submit Inquiry
                    <Send className="w-4 h-4" />
                  </span>
                </button>
              </form>
            </motion.div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Contact;
